import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import SustainabilityAreaIcon from '@app/src/pages/TopicalRisk/SustainabilityAreaIcon'
import type { AnswerFlagArea, SustainabilityArea } from '@app/src/types/resourceExplorer'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import { alpha, Divider, List, ListItemButton, ListItemText, Paper, Stack, Typography, useTheme } from '@mui/material'
import * as React from 'react'
import { useIntl } from 'react-intl'

export interface RiskAreaItem {
  id: number | string
  title: string
  riskStatus: RiskStatus
  onClick?: () => void
}

interface RiskAreaCardProps {
  area: SustainabilityArea | typeof AnswerFlagArea
  title: string
  items: RiskAreaItem[]
}

const RiskAreaCard = ({ area, title, items }: RiskAreaCardProps) => {
  const { palette } = useTheme()
  const providerType = useCurrentProviderType()
  const { formatMessage } = useIntl()

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '6px', height: '100%', borderColor: 'grey.200' }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <SustainabilityAreaIcon area={area} sx={{ color: alpha(palette.primary.main, 0.6) }} />
        <Typography variant="subtitle1">{title}</Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {items.length === 0 ? (
        <Typography variant="body2" color="text.disabled" pt={1.1}>
          {formatMessage({ id: 'topicalRisk.nothingToShow' })}
        </Typography>
      ) : (
        <List disablePadding>
          {items.map(item => (
            <ListItemButton
              key={item.id}
              onClick={item.onClick}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '6px',
                mx: -1.1,
                px: 1.1,
                pr: 0.9,
                mr: -0.9,
                py: 0.8,
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  variant: 'body2',
                  lineHeight: 1.6,
                }}
                sx={{ pr: 2 }}
              />
              <SimpleRiskChip
                riskStatus={item.riskStatus}
                tooltip={
                  item.riskStatus === RiskStatus.NotApplicable
                    ? formatMessage({ id: 'topicalRisk.noDataTooltip' }, { providerType })
                    : undefined
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  )
}

export default RiskAreaCard
